import { i18n } from '@/i18n'
import { SettingsService } from '@/services/settings'
import type {
  InstantWinDraw,
  InstantWinDrawEntry,
  InstantWinDrawTickets,
  InstantWinWinnerEntries,
  InstantWinWinnerEntriesWithReference,
  Product,
} from '@/types'
import { capitalCase, kebabCase } from 'change-case'
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber'
import { DateTime } from 'luxon'

import {
  Countries,
  InstantWinPrizeType,
  PlanTiers,
  PrizeCategories,
  ProductTypes,
  type TPlanTier,
  type TProductType,
} from './helper-objects'
import { isE164PhoneNumber } from './input-validator-utils'
import { isDeposit, isInstantWinDraw, isPlan } from './type-guard-utils'

export const pluralise = (i18nKey: string, count: number): string => {
  const message = i18n.global.t(i18nKey, { count })
  // Replace the numeric value with formatted version
  const formatted = i18n.global.n(count, 'number')

  return message.replace(count.toString(), formatted)
}

/**
 * Compares two values of type string, number, or DateTime.
 *
 * If both values are DateTime objects, the difference in milliseconds is returned.
 * Otherwise, the values are compared as strings using localeCompare with 'en-GB' locale, numeric usage, and base sensitivity (case-insensitive).
 *
 * @template T - The type of the values to compare. Defaults to string, number, or DateTime.
 * @param {T} v1 - The first value to compare.
 * @param {T} v2 - The second value to compare.
 * @returns {number} - Returns a negative number if v1 is less than v2, zero if they are equal, or a positive number if v1 is greater than v2.
 */
export const compareFn = <T = string | number | DateTime>(
  v1: T,
  v2: T
): number => {
  if (DateTime.isDateTime(v1) || DateTime.isDateTime(v2)) {
    const v1Datetime = DateTime.isDateTime(v1) ? v1 : DateTime.fromISO(`${v1}`)
    const v2Datetime = DateTime.isDateTime(v2) ? v2 : DateTime.fromISO(`${v2}`)

    if (!v1Datetime.isValid || !v2Datetime.isValid) {
      throw new Error('Invalid DateTime value')
    }

    return v2Datetime.diff(v1Datetime).milliseconds === 0
      ? 0
      : v2Datetime.diff(v1Datetime).milliseconds < 0
        ? 1
        : -1
  }

  return `${v1 as string | number}`.localeCompare(
    `${v2 as string | number}`,
    new Intl.Locale('en-GB'),
    {
      usage: 'search',
      numeric: true,
      sensitivity: 'base',
    }
  )
}

/**
 * Flips an object's values for it's keys (both must be strings).
 *
 * @param {Record<string | number, string | number>} obj
 * @returns {Record<string | number, string | number>}
 */
export const flipObject = (
  obj: Record<string | number, string | number>
): Record<string | number, string | number> => {
  return Object.fromEntries(Object.entries(obj).map((o) => o.reverse()))
}

/**
 * Groups an array of items by a key callback.
 *
 * @param {(item: T) => string} keyFn
 * @param {Array<T>} items
 * @returns {Record<string, T[]>}
 */
export const groupBy = <T>(
  keyFn: (item: T) => string | number,
  items: Array<T>
): Record<string, T[]> => {
  const groupMap = new Map()

  for (const item of items) {
    const key = keyFn(item)

    const group = groupMap.get(key) ?? []

    group.push(item)

    groupMap.set(key, group)
  }

  return Object.fromEntries(groupMap)
}

/**
 * Checks if a value is empty.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isEmpty = (value: any): boolean => {
  if (value == null || value === false) {
    return true
  } // Checks for null and undefined or false

  if (typeof value === 'string' && value.trim().length === 0) {
    return true
  } // Checks for empty string

  if (Array.isArray(value) && value.length === 0) {
    return true
  } // Checks for empty array

  if (typeof value === 'object' && Object.keys(value).length === 0) {
    return true
  } // Checks for empty object

  return false
}

/**
 * Wraps a value in an array if it is not already an array.
 *
 * @param {T | Array<T>} value - The value to wrap in an array.
 * @returns {Array<T>} - The value wrapped in an array, or the original array if the value is already an array.
 */
export const arrayWrap = <T>(value: T | Array<T>): Array<T> =>
  Array.isArray(value) ? value : [value]

type RecursiveMapType =
  | undefined
  | null
  | number
  | string
  | boolean
  | Array<any>
  | Set<any>
  | Map<any, any>
  | { [key: string]: any }

/**
 * Maps all values in an object according to the callback.
 *
 * @param {Record<string, T>} obj
 * @param {(value: any, key?: string) => any} fn
 * @returns {T}
 */
export const mapRecursive = (
  val: RecursiveMapType,
  fn: (value: any, key?: string | number) => any
): RecursiveMapType => {
  if (val instanceof Map) {
    // val is a map
    const newMap = new Map()

    for (const [key, value] of val) {
      // If the value is a Map, recursively map its values
      if (value instanceof Map) {
        newMap.set(key, mapRecursive(value, fn)) // Recursive call
      } else {
        newMap.set(key, fn(value, key)) // Apply callback to the value
      }
    }

    return newMap
  } else if (val instanceof Set) {
    // val is a set
    return new Set(
      [...val].map((value) => {
        // If the value is a Set itself, recursively map its values
        if (value instanceof Set) {
          return mapRecursive(value, fn)
        }
        // Otherwise, apply the callback to the value
        return fn(value)
      })
    )
  } else if (Array.isArray(val)) {
    // val is an array
    return val.map((value, index) => {
      if (
        value instanceof Set ||
        value instanceof Map ||
        Array.isArray(value) ||
        (typeof value === 'object' && value !== null)
      ) {
        return mapRecursive(value, fn) // Recursion
      } else {
        return fn(value, index)
      }
    })
  } else if (val instanceof Object) {
    // val is an object
    return Object.keys(val).reduce(
      (acc, key) => {
        const value = val[key]

        // If the value is an object, recursively map its values
        if (value && typeof value === 'object') {
          acc[key] = mapRecursive(value, fn) // Recursive call
        } else {
          acc[key] = fn(value, key) // Apply callback to the value
        }

        return acc
      },
      {} as Record<string, any>
    )
  } else {
    // val is a primitive value
    return fn(val)
  }
}

/**
 * Compares plans (by tier) testing if one plan is higher than the other.
 *
 * @param {TPlanTier} planTier
 * @param {TPlanTier} comparativePlanTier
 * @returns {boolean}
 */
export const isAvailableInPlan = (
  planTier: TPlanTier,
  comparativePlanTier: TPlanTier
): boolean => {
  return PlanTiers[planTier] >= PlanTiers[comparativePlanTier]
}

/**
 * Returns the plan tier of the next highest plan.
 *
 * @param {TPlanTier} planTier
 * @returns {TPlanTier}
 */
export const nextAvailablePlan = (planTier: TPlanTier): TPlanTier => {
  const currentTier = PlanTiers[planTier]
  const nextTier = currentTier + 1

  const lastTier = Math.max(...Object.values(PlanTiers))

  if (currentTier >= lastTier) {
    return planTier
  }

  // Get all plans from the next tier
  return Object.entries(PlanTiers).filter(
    ([, tierValue]) => tierValue === nextTier
  )?.[0]?.[0] as TPlanTier
}

/**
 * Formats a phone number to E.164 format.
 * If the phone number is not in E.164 format, it will return the original phone number.
 *
 * @param {string | number} phoneNumber
 * @returns {string}
 */
export const formatToPhoneNumber = (phoneNumber: string | number): string => {
  if (isE164PhoneNumber({ name: 'phone', value: phoneNumber }) === true) {
    const phoneUtil = new PhoneNumberUtil()

    return SettingsService.getCurrencyCode() === 'GBP'
      ? phoneUtil.format(
          phoneUtil.parse(`${phoneNumber}`, Countries.UnitedKingdom),
          PhoneNumberFormat.E164
        )
      : phoneUtil.format(
          phoneUtil.parse(`${phoneNumber}`, Countries.Ireland),
          PhoneNumberFormat.E164
        )
  }

  return `${phoneNumber}`
}

export const toValue = (value: any | (() => any)): any => {
  if (isEmpty(value)) {
    return value
  }

  return typeof value === 'function' ? value() : value
}

/**
 * Groups all instant draw's winning entries by the draw.
 *
 * @param {Record<string, Array<InstantWinDrawTickets>>} tickets
 * @param {keyof InstantWinWinnerEntries} groupBy
 * @returns {Array<InstantWinDrawEntry>}
 */
export const groupInstantWinWinnersByDraw = (
  tickets: Record<string, Array<InstantWinDrawTickets>>,
  groupBy: keyof InstantWinWinnerEntries = 'draw_reference_id'
): Array<InstantWinDrawEntry> => {
  return [
    ...Object.values(tickets)
      // Flatten all instant draws to winning entries
      .flatMap((instantWinDraw: Array<InstantWinDrawTickets>) => {
        return instantWinDraw
          .flatMap((draw) => draw.winning_entries)
          .filter(
            (drawTickets) =>
              drawTickets.prize_type === InstantWinPrizeType.Entry &&
              drawTickets[groupBy]
          )
      })
      // Group by draw reference id and create a map of draw entries with count
      .reduce((entryMap, entry) => {
        const entryWithReference = entry as InstantWinWinnerEntriesWithReference

        if (!entryMap.has(entryWithReference.draw_reference_id)) {
          entryMap.set(entryWithReference.draw_reference_id, {
            count: 0,
            drawLabel: entryWithReference.prize_name,
            drawnAt: entryWithReference.drawn_at,
            prizeType: entryWithReference.prize_type,
            tickets: [],
          })
        }

        const instantWinDrawEntry = entryMap.get(
          entryWithReference.draw_reference_id
        ) as InstantWinDrawEntry

        instantWinDrawEntry.count += entryWithReference.prize_quantity
        instantWinDrawEntry.tickets.push(entryWithReference)

        return entryMap
      }, new Map<number, InstantWinDrawEntry>())
      .values(),
  ]
}

/**
 * Scrambles the elements of an array in random order.
 *
 * @param arr - The array to be scrambled.
 * @returns A new array with the elements of the input array in random order.
 *
 */
export const scrambleArray = (arr: any[]) => {
  const result = [...arr]
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp = result[i]
    result[i] = result[j]
    result[j] = temp
  }
  return result
}

/*
 * Checks if a word should be prefixed with 'a' or 'an'.
 *
 * @param {string} word
 * @returns {string}
 */
export const aOrAn = (word: string): string => {
  const specialCasesAn = [
    'honest',
    'hour',
    'honor',
    'heir',
    'MBA',
    'MSc',
    'FBI',
    'FDA',
    'NHL',
    'X-ray',
    'AI',
    'MRI',
    'HTML',
    'FAQ',
    'LPC',
    'SUV',
  ]

  const specialCasesA = [
    'university',
    'European',
    'unicorn',
    'useful',
    'one',
    'once',
    'UFO',
    'U.S.',
    'URL',
  ]

  if (word.startsWith('£') || word.startsWith('€')) {
    return word
  }

  if (/^h[aeiou]/i.test(word) && !specialCasesAn.includes(word)) {
    return `an ${word}`
  }

  if (
    specialCasesAn.some((special) =>
      word.toLowerCase().startsWith(special.toLowerCase())
    )
  ) {
    return `an ${word}`
  }

  if (
    specialCasesA.some((special) =>
      word.toLowerCase().startsWith(special.toLowerCase())
    )
  ) {
    return `a ${word}`
  }

  return (/^[aeiou]/i.test(word) ? 'an' : 'a') + ` ${word}`
}

/**
 * Generates a tooltip string indicating the availability of a benefit with specified plans.
 *
 * @param plans Array of plan tiers
 * @returns {string} A tooltip string indicating the availability of the benefit with the specified plans.
 */
export const benefitPlansTooltip = (plans: Array<TPlanTier>): string => {
  if (plans.length) {
    let i18nPlans = plans.map((plan) => {
      return i18n.global.t('membership_badge.' + plan.toLocaleLowerCase())
    })

    if (i18nPlans.length >= 2) {
      i18nPlans = i18nPlans.map((plan, index) => {
        if (index === i18nPlans.length - 1) {
          return `or ${plan}`
        }
        return plan
      })
    }

    const planString =
      i18nPlans.length > 2 ? i18nPlans.join(', ') : i18nPlans.join(' ')

    return `This benefit is available with ${plans.length > 1 ? planString + ' memberships' : aOrAn(planString) + ' membership'}.`
  }
  return 'This benefit is available with all memberships.'
}

/**
 * Returns a `Promise` the will resolve after a specified duration
 *
 * @param duration Number of milliseconds to wait
 * @returns A promise that will resolve after the specified duration
 */
export const delay = (duration: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, duration))
}

/**
 * Filters and sorts instant win games.
 *
 * - Closed games (past `close_at`) are excluded when there are more than `minGames`.
 * - `featuredOnly`: shows only featured games sorted by `display_order`.
 *   Falls back to prize-value ordering if no games are featured.
 * - `orderByPrizeValue`: sorts in two tiers — games with high-value individual
 *   prizes (>= `highValueThreshold`, default £500) first, then the rest.
 *   Within each tier, sorted by total remaining prize value (highest first).
 *   Sold-out games naturally end up last since their remaining value is 0.
 *
 * @param {Array<InstantWinDraw>} games
 * @param {Object} config
 * @returns {Array<InstantWinDraw>}
 */
/**
 * Calculate the remaining prize value for a game.
 * Sums up `value * (prizes_available_count - prizes_won_count)` for each prize.
 */
const getRemainingPrizeValue = (game: InstantWinDraw): number => {
  if (!game.prizes?.length) {
    return 0
  }
  const value = game.prizes.reduce((sum, prize) => {
    const remaining =
      (prize.prizes_available_count ?? 0) - (prize.prizes_won_count ?? 0)
    return sum + (prize.value ?? 0) * Math.max(remaining, 0)
  }, 0)
  return value
}

/**
 * Check if a game has any remaining prize with a value at or above a threshold.
 */
const hasHighValuePrize = (
  game: InstantWinDraw,
  threshold: number
): boolean => {
  return (
    game.prizes?.some((prize) => {
      const remaining =
        (prize.prizes_available_count ?? 0) - (prize.prizes_won_count ?? 0)
      return remaining > 0 && (prize.value ?? 0) >= threshold
    }) ?? false
  )
}

/**
 * Creates a comparator that sorts games in two tiers:
 * 1. Games with at least one remaining high-value prize (>= threshold) come first.
 * 2. Games without any high-value prizes come second.
 * Within each tier, games are sorted by total remaining prize value (highest first).
 */
const createPrizeValueComparator = (highValueThreshold: number) => {
  return (a: InstantWinDraw, b: InstantWinDraw): number => {
    const aHasHighValue = hasHighValuePrize(a, highValueThreshold)
    const bHasHighValue = hasHighValuePrize(b, highValueThreshold)

    // High-value games come first
    if (aHasHighValue !== bHasHighValue) {
      return aHasHighValue ? -1 : 1
    }

    // Within the same tier, sort by total remaining prize value
    return getRemainingPrizeValue(b) - getRemainingPrizeValue(a)
  }
}

export const filterGames = (
  games: Array<InstantWinDraw>,
  config: {
    minGames?: number
    maxProgress?: number
    /**
     * When true, filters to only featured games (is_featured === true) and
     * sorts them by display_order. If no featured games exist, falls back
     * to showing all games sorted by remaining prize value.
     */
    featuredOnly?: boolean
    /**
     * When true, sorts games by remaining prize value (highest first).
     * Games with high-value individual prizes (>= `highValueThreshold`) are
     * prioritised above games with only lower-value prizes.
     * Sold-out games naturally end up last since their remaining value is 0.
     */
    orderByPrizeValue?: boolean
    /**
     * Minimum individual prize value (in £) for a game to be considered
     * "high-value" and sorted into the top tier. Defaults to 500.
     */
    highValueThreshold?: number
  } = {
    minGames: 4,
    maxProgress: 100,
    highValueThreshold: 500,
  }
): Array<InstantWinDraw> => {
  const filteredGames =
    games.length > (config.minGames ?? 4)
      ? games.filter((game: InstantWinDraw) => {
          // If the game is closed, it is not available
          if (game.main_prize.close_at.diffNow().milliseconds < 0) {
            return false
          }

          return true
        })
      : games

  // Featured-only mode:
  // Falls back to prize-value ordering if no featured games exist.
  if (config.featuredOnly) {
    const featuredGames = filteredGames.filter((g) => g.is_featured === true)

    if (featuredGames.length > 0) {
      return featuredGames.sort(
        (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)
      )
    }

    // Fallback: no featured games — sort all by remaining prize value
    const fallbackComparator = createPrizeValueComparator(
      config.highValueThreshold ?? 500
    )
    return [...filteredGames].sort(fallbackComparator)
  }

  // Prize-value ordering mode (for category tabs)
  if (config.orderByPrizeValue) {
    const comparator = createPrizeValueComparator(
      config.highValueThreshold ?? 500
    )
    return [...filteredGames].sort(comparator)
  }

  return filteredGames
}

/**
 * Format prize categories for terms and conditions.
 * Removes "Featured" category and capitalizes/singularizes remaining categories.
 * Returns a new array without mutating the original.
 *
 * @param {string[]} categories - The prize categories to format.
 * @returns {string[]} - Formatted categories suitable for display.
 */
export const formatPrizeCategoriesForTermsAndConditions = (
  categories: string[]
): string[] =>
  categories
    .filter((c) => c !== PrizeCategories.Featured)
    .map((c) => capitalCase(c).replace(/s$/, ''))

/**
 * Generates a random number of a specified size.
 *
 * @param {number} size
 * @returns {string}
 */
export const getRandomNumber = (size: number = 16): string => {
  const randomChar = () => Math.round(16 * Math.random()).toString(16)
  return 'x'.repeat(size).replace(/x/g, randomChar)
}

/**
 * Generates a secure password.
 *
 * @param length The length of the password
 * @returns A secure password
 */
export const generateSecurePassword = (length: number = 12): string => {
  const lowercase = 'abcdefghijklmnopqrstuvwxyz'
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const numbers = '0123456789'
  const special = '!@#$%^&*()_+-=[]{}|;:,.<>?'

  // Ensure at least one of each required character type
  const password = [
    lowercase[Math.floor(Math.random() * lowercase.length)],
    uppercase[Math.floor(Math.random() * uppercase.length)],
    numbers[Math.floor(Math.random() * numbers.length)],
    special[Math.floor(Math.random() * special.length)],
  ]

  // Fill the rest with random characters from all possible characters
  const allChars = lowercase + uppercase + numbers + special
  for (let i = password.length; i < length; i++) {
    password.push(allChars[Math.floor(Math.random() * allChars.length)])
  }

  // Shuffle the array to make the pattern less predictable
  for (let i = password.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[password[i], password[j]] = [password[j], password[i]]
  }

  return password.join('')
}

/**
 * Sets the `aria-labelledby` attribute on an element based on its heading - if missing.
 *
 * @param {HTMLElement} el
 * @returns {boolean}
 */
export const setLabelledByAttribute = (el: HTMLElement): boolean => {
  if (!el.hasAttribute('aria-labelledby')) {
    const headings = el.querySelectorAll('h1, h2, h3, h4, h5, h6')

    if (headings.length) {
      const label = headings.item(0)
      const labelledby = !label.hasAttribute('id')
        ? label.setAttribute(
            'id',
            `heading-${kebabCase(label.textContent ?? '')}`
          )
        : label.getAttribute('id')

      el.setAttribute('aria-labelledby', labelledby as string)

      return true
    } else {
      console.warn('ARIA Issue: Missing `aria-labelledby` element.', el)
    }
  }
  return false
}

/**
 * Gets the product type for a given product.
 *
 * @param product - The product to check.
 * @returns The product type.
 */
export const getProductType = (product: Product): TProductType => {
  if (isPlan(product)) {
    return ProductTypes.Subscription
  } else if (isInstantWinDraw(product)) {
    return ProductTypes.InstantWinDraw
  } else if (isDeposit(product)) {
    return ProductTypes.Deposit
  }

  throw new Error('Unknown product type')
}

/**
 * Closes all open modals (both HTML dialog elements and div-based modals).
 *
 * @returns {void}
 */
export const closeAllModals = (): void => {
  // Close all dialog-based modals
  const dialogModals = document.querySelectorAll('dialog[open]')
  dialogModals.forEach((modal) => {
    if (modal instanceof HTMLDialogElement) {
      modal.close()
      modal.classList.remove('is-open')
    }
  })

  // Close all div-based modals
  const divModals = document.querySelectorAll('div[id*="modal"].is-open')
  divModals.forEach((modal) => {
    modal.classList.remove('is-open')
  })
}

/**
 * Get the computed style of an element for a specific property.
 *
 * Required due to Firefox not supporting computedStyleMap on all elements.
 *
 * @see https://caniuse.com/?search=computedStyleMap
 * @param {Element} element
 * @param {string} property
 * @returns {string | undefined}
 */
export const getComputedStyle = (
  element: Element,
  property: string
): undefined | string => {
  if ('computedStyleMap' in element) {
    return element.computedStyleMap().get(property)?.toString()
  }

  // Handle grid-template-columns and grid-template-rows separately since
  // window.getComputedStyle returns the grid-template-* calculated pixel values
  if (property.startsWith('grid-template-')) {
    const gridTemplate = window
      .getComputedStyle(element)
      .getPropertyValue('grid-template')

    return gridTemplate === 'none'
      ? 'none'
      : gridTemplate
          ?.split('/')
          [property === 'grid-template-columns' ? 1 : 0]?.trim()
  }

  return window.getComputedStyle(element).getPropertyValue(property)
}

/**
 * Extracts referral code and identifier from a referral URL.
 * Supports both legacy draw IDs and partnership campaign IDs.
 *
 * @param {string} referralUrl
 * @returns
 *  | { referralCode: string; partnershipCampaignId: number; drawId: number }
 *  | { referralCode: null; partnershipCampaignId: null; drawId: null }
 */
export const extractDetailsFromReferralUrl = (
  referralUrl: string
):
  | { referralCode: string; referralId: number }
  | { referralCode: null; referralId: null } => {
  if (!referralUrl) {
    return { referralCode: null, referralId: null }
  }

  try {
    const url = new URL(referralUrl, window.location.origin)
    const pathSegments = url.pathname.split('/').filter((segment) => segment)

    let referralPart: string | undefined

    if (pathSegments.length === 1) {
      // Legacy subdomain format: https://r.goodlifeplus.co.uk/{referralCode}-{id}
      referralPart = pathSegments[0]
    } else if (
      pathSegments.length === 2 &&
      (pathSegments[0] === 'r' || pathSegments[0] === 'share')
    ) {
      // New path-based format: https://{domain}/r/{value} or https://{domain}/share/{value}
      referralPart = pathSegments[1]
    }

    if (referralPart) {
      const [referralCode, idStr] = referralPart.split(/-(\d+)$/)

      const referralIdInt = parseInt(idStr, 10)

      if (referralCode && !isNaN(referralIdInt)) {
        return {
          referralCode,
          referralId: referralIdInt,
        }
      }
    }
  } catch {
    return { referralCode: null, referralId: null }
  }

  return { referralCode: null, referralId: null }
}
